import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Inbox, ClipboardList, Target, AlertOctagon, Save, Loader2,
  CheckCircle2, AlertTriangle, RefreshCw, FileText, Download,
  ListChecks, Hourglass, Plus, MessageSquare, ShieldCheck, RotateCcw,
  Package, FilePlus, Trash2, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { HudCard, HudButton, HudBadge } from '../hud';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

/**
 * Doc-types considérés comme "Fichiers reçus du client" (entrée brute).
 * Le reste (plan_lot, façade…) est considéré comme travail interne et
 * reste filtré dans Paramétrage projet > Fichiers.
 */
const CLIENT_DOC_TYPES = new Set([
  'cctp', 'plan_masse', 'piece_ecrite', 'photo', 'other',
  // Apartment_single + AI generator refs
  'etat_actuel', 'inspiration', 'ref_materiau', 'ref_mobilier', 'ref_visuel',
]);

const DOC_TYPE_LABELS = {
  cctp:         'CCTP',
  plan_masse:   'Plan de masse',
  piece_ecrite: 'Pièce écrite',
  photo:        'Photo / 3D',
  other:        'Autre',
  etat_actuel:  'État actuel',
  inspiration:  'Inspiration',
  ref_materiau: 'Réf. matériau',
  ref_mobilier: 'Réf. mobilier',
  ref_visuel:   'Réf. générateur IA',
};

// Categories the user can pick from this row's dropdown
const RECATEGORIZABLE_TYPES = [
  'ref_visuel', 'inspiration', 'ref_materiau', 'ref_mobilier',
  'etat_actuel', 'photo', 'cctp', 'plan_masse', 'piece_ecrite', 'other',
];

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

/**
 * ProjectBriefSection — V14.2 / Sprint C.
 *
 * Section "Demande projet" : ce que le client a transmis avant toute
 * configuration interne. Trois zones :
 *  1) Brief client éditable (description + 3 nouveaux champs free-text
 *     stockés dans `general_info`)
 *  2) Fichiers reçus (filtrés sur les doc_types côté entrée) — LECTURE SEULE
 *     (la qualification se fait dans l'onglet "Fichiers", source unique)
 *  3) Itérations & validations (placeholder MVP)
 */
const ProjectBriefSection = ({ project, projectId, onSaved, onOpenFilesTab }) => {
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);

  // Init form from project
  useEffect(() => {
    if (!project) return;
    const gi = project.general_info || {};
    const initial = {
      description: project.description || '',
      brief_exigences:   gi.brief_exigences   || '',
      brief_objectifs:   gi.brief_objectifs   || '',
      brief_contraintes: gi.brief_contraintes || '',
    };
    setForm(initial);
    setOriginal(initial);
  }, [project]);

  // Load files for this project
  const loadFiles = useCallback(async () => {
    if (!projectId) return;
    setLoadingFiles(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/files`, opts);
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setFiles(items);
    } catch {
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [projectId]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const clientFiles = useMemo(
    () => files.filter((f) => CLIENT_DOC_TYPES.has(f.doc_type || 'other')),
    [files]
  );

  const filesByType = useMemo(() => {
    const map = {};
    clientFiles.forEach((f) => {
      const k = f.doc_type || 'other';
      (map[k] = map[k] || []).push(f);
    });
    return map;
  }, [clientFiles]);

  const isDirty = JSON.stringify(form) !== JSON.stringify(original);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const general_info = {
        ...(project.general_info || {}),
        brief_exigences:   form.brief_exigences?.trim() || '',
        brief_objectifs:   form.brief_objectifs?.trim() || '',
        brief_contraintes: form.brief_contraintes?.trim() || '',
      };
      const payload = {
        description: form.description?.trim() || '',
        general_info,
      };
      const r = await axios.put(`${API}/projects/${projectId}`, payload, opts);
      setOriginal(form);
      toast.success('Demande projet enregistrée');
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="flex items-center gap-2 text-[13px] py-12 justify-center" style={{ color: 'var(--c-text-2)' }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div className="hud-stagger space-y-5" data-testid="project-brief-section">
      {/* ============================================================ */}
      {/* Bloc 1 — Brief client                                          */}
      {/* ============================================================ */}
      <HudCard variant="glass" bracket>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Demande client</span>
            <p
              className="mt-1 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
            >
              Brief & cadre de la commande
            </p>
            <p className="text-[12.5px] mt-1 max-w-2xl" style={{ color: 'var(--c-text-2)' }}>
              Ce que le client attend. Sert de référence interne pendant
              tout le projet — visible dans le Hub, le rapport PDF et le
              dossier commercial.
            </p>
          </div>
          <Inbox className="w-5 h-5 shrink-0" style={{ color: 'var(--c-gold)' }} />
        </div>

        <BriefField
          label="Brief général"
          icon={ClipboardList}
          value={form.description}
          onChange={(v) => setField('description', v)}
          placeholder="Pitch commercial, contexte, particularités du programme…"
          testid="brief-description"
          rows={4}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <BriefField
            label="Objectifs visés"
            icon={Target}
            value={form.brief_objectifs}
            onChange={(v) => setField('brief_objectifs', v)}
            placeholder="ex. Présentation aux investisseurs, dépôt PC, pré-commercialisation VEFA…"
            testid="brief-objectifs"
            rows={3}
          />
          <BriefField
            label="Exigences spécifiques"
            icon={ListChecks}
            value={form.brief_exigences}
            onChange={(v) => setField('brief_exigences', v)}
            placeholder="Style architectural, vues prioritaires, ambiance, matériaux signature…"
            testid="brief-exigences"
            rows={3}
          />
        </div>

        <div className="mt-4">
          <BriefField
            label="Contraintes & points de vigilance"
            icon={AlertOctagon}
            value={form.brief_contraintes}
            onChange={(v) => setField('brief_contraintes', v)}
            placeholder="Mitoyennetés, PLU, calendrier serré, validation comité, NDA…"
            testid="brief-contraintes"
            rows={2}
          />
        </div>
      </HudCard>

      {/* ============================================================ */}
      {/* Bloc 2 — Fichiers reçus                                        */}
      {/* ============================================================ */}
      <HudCard variant="glass">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Pièces reçues</span>
            <p
              className="mt-1 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
            >
              Fichiers transmis par le client
            </p>
            <p className="text-[12.5px] mt-1" style={{ color: 'var(--c-text-2)' }}>
              Aperçu en lecture seule. Pour qualifier (catégorie, bâtiment, étage, lot) ou
              <strong> étoiler ⭐ une référence pour la génération 3D</strong>, ouvrez l'onglet
              <strong> Fichiers</strong>.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onOpenFilesTab && (
              <HudButton
                variant="ghost"
                size="sm"
                onClick={onOpenFilesTab}
                leftIcon={<FileText className="w-3.5 h-3.5" />}
                data-testid="brief-open-files-tab-btn"
              >
                Gérer les fichiers
              </HudButton>
            )}
            <FileText className="w-5 h-5 shrink-0" style={{ color: 'var(--c-gold)' }} />
          </div>
        </div>

        {loadingFiles ? (
          <div className="flex items-center gap-2 text-[13px] py-8 justify-center" style={{ color: 'var(--c-text-2)' }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement des fichiers…
          </div>
        ) : clientFiles.length === 0 ? (
          <div
            className="rounded-lg py-10 text-center"
            style={{ background: 'var(--c-surface-2)', border: '1px dashed var(--c-border)' }}
            data-testid="brief-files-empty"
          >
            <Inbox className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--c-text-muted)' }} />
            <p className="text-[13px] font-medium" style={{ color: 'var(--c-text)' }}>
              Aucune pièce reçue
            </p>
            <p className="text-[11.5px] mt-1" style={{ color: 'var(--c-text-muted)' }}>
              Téléversez le CCTP, le plan de masse ou les photos du client
              depuis l'onglet Paramétrage projet.
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="brief-files-list">
            {Object.entries(filesByType).map(([type, group]) => (
              <div key={type}>
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="hud-eyebrow"
                    style={{ color: 'var(--c-text-muted)' }}
                  >
                    {DOC_TYPE_LABELS[type] || type}
                  </span>
                  <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">
                    {group.length}
                  </Badge>
                </div>
                <ul className="space-y-1.5">
                  {group.map((f) => (
                    <ClientFileRow key={f.file_id} file={f} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </HudCard>

      {/* ============================================================ */}
      {/* Bloc 3 — Itérations & validations                              */}
      {/* ============================================================ */}
      <IterationsTimeline projectId={projectId} project={project} />

      {/* ============================================================ */}
      {/* Save bar (sticky bottom)                                       */}
      {/* ============================================================ */}
      <div
        className="sticky bottom-3 z-10 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap"
        style={{
          background: 'rgba(14,17,22,0.92)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--c-border)',
          boxShadow: '0 12px 32px -12px rgba(0,0,0,0.6)',
        }}
        data-testid="brief-save-bar"
      >
        <div className="flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--c-text-2)' }}>
          {isDirty ? (
            <>
              <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--c-gold)' }} />
              <span>Modifications non enregistrées</span>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#86EFAC' }} />
              <span>Brief à jour</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <HudButton
            variant="ghost"
            size="sm"
            onClick={() => setForm(original)}
            disabled={!isDirty || saving}
            leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
            data-testid="brief-reset-btn"
          >
            Annuler
          </HudButton>
          <HudButton
            variant="primary"
            size="sm"
            onClick={save}
            disabled={!isDirty || saving}
            leftIcon={saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            data-testid="brief-save-btn"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer le brief'}
          </HudButton>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------- */
/* Sub-components                                              */
/* ---------------------------------------------------------- */

const BriefField = ({ label, icon: Icon, value, onChange, placeholder, rows = 3, testid }) => (
  <div>
    <label className="hud-eyebrow flex items-center gap-1.5 mb-1.5">
      {Icon && <Icon className="w-3 h-3" style={{ color: 'var(--c-text-muted)' }} />}
      {label}
    </label>
    <textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={testid}
      className="w-full rounded-lg p-3 text-[13px] outline-none resize-y leading-relaxed"
      style={{
        background: 'var(--c-surface-2)',
        border: '1px solid var(--c-border)',
        color: 'var(--c-text)',
      }}
    />
  </div>
);

const ClientFileRow = ({ file }) => {
  const downloadUrl = `${API}/files/${file.file_id}/download`;
  const fmtSize = (n) => {
    if (!n) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };
  const displayName = file.original_filename || file.original_name || file.filename || file.file_id;
  const catLabel = DOC_TYPE_LABELS[file.doc_type] || file.doc_type || 'Non catégorisé';
  // ⭐ marker — file is retained → injected into 3D generation (image refs only).
  // Mirrors the logic of blueprint_3d_render_router._load_starred_client_refs.
  const isClientRef = (file.content_type || '').startsWith('image/')
    && ['ref_visuel', 'ref_materiau', 'ref_mobilier', 'inspiration'].includes(file.doc_type);
  const isStarred = !!file.is_retained && isClientRef;
  return (
    <li
      className="rounded-md flex items-center gap-3 px-3 py-2 hover:bg-white/[0.02] transition-colors"
      style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
      data-testid={`brief-file-row-${file.file_id}`}
    >
      <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--c-text-muted)' }} />
      <span
        className="text-[12.5px] flex-1 min-w-0 truncate"
        style={{ color: 'var(--c-text)' }}
        title={displayName}
      >
        {displayName}
      </span>
      {isStarred && (
        <span
          title="Étoilée — injectée dans la prochaine génération 3D"
          data-testid={`brief-file-starred-${file.file_id}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0"
          style={{
            background: 'rgba(217,160,91,0.14)',
            border: '1px solid rgba(217,160,91,0.45)',
            color: '#FBBF24',
          }}
        >
          ⭐ Réf. 3D
        </span>
      )}
      <span
        data-testid={`brief-file-cat-${file.file_id}`}
        className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium shrink-0"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid var(--c-border)',
          color: 'var(--c-text-2)',
        }}
      >
        {catLabel}
      </span>
      <span className="hud-tabular text-[10.5px] shrink-0" style={{ color: 'var(--c-text-muted)' }}>
        {fmtSize(file.size)}
      </span>
      <span className="text-[10.5px] shrink-0" style={{ color: 'var(--c-text-faint)' }}>
        {fmtDateTime(file.created_at).split(',')[0]}
      </span>
      <a
        href={downloadUrl}
        className="p-1 rounded hover:bg-white/[0.06] transition-colors shrink-0"
        title="Télécharger"
        data-testid={`brief-file-download-${file.file_id}`}
      >
        <Download className="w-3.5 h-3.5" style={{ color: 'var(--c-text-muted)' }} />
      </a>
    </li>
  );
};

export default ProjectBriefSection;

/* ============================================================ */
/* IterationsTimeline — vraie timeline des itérations & validations */
/* V14.2 / Sprint C Phase 3                                          */
/* ============================================================ */

const TYPE_META = {
  brief_update: { label: 'Brief mis à jour', icon: ClipboardList, color: '#3B82F6' },
  review:       { label: 'Revue client',     icon: MessageSquare, color: '#D9A05B' },
  validation:   { label: 'Validation',       icon: ShieldCheck,   color: '#22C55E' },
  retake:       { label: 'Demande retake',   icon: RotateCcw,     color: '#F97316' },
  delivery:     { label: 'Livraison',        icon: Package,       color: '#A855F7' },
  note:         { label: 'Note',             icon: FilePlus,      color: '#64748B' },
};

const STATUS_META = {
  en_attente:   { label: 'En attente',   tone: 'neutral' },
  validee:      { label: 'Validée',      tone: 'success' },
  a_reprendre:  { label: 'À reprendre',  tone: 'warning' },
  rejetee:      { label: 'Rejetée',      tone: 'danger'  },
};

const IterationsTimeline = ({ projectId, project }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Lookups for attachment pickers (files + generations) — loaded once
  const [files, setFiles] = useState([]);
  const [generations, setGenerations] = useState([]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [iters, fsRes, gensRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/iterations`, opts).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/projects/${projectId}/files`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/projects/${projectId}/generations`, opts).catch(() => ({ data: { items: [] } })),
      ]);
      setItems(iters.data?.items || []);
      setFiles(Array.isArray(fsRes.data) ? fsRes.data : (fsRes.data?.items || []));
      setGenerations(Array.isArray(gensRes.data) ? gensRes.data : (gensRes.data?.items || []));
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const fileLookup = useMemo(() => {
    const m = {};
    files.forEach((f) => { m[f.file_id] = f; });
    return m;
  }, [files]);

  const genLookup = useMemo(() => {
    const m = {};
    generations.forEach((g) => { m[g.job_id || g.generation_id] = g; });
    return m;
  }, [generations]);

  const openCreate = () => setEditing({
    type: 'review', status: 'en_attente', title: '', description: '',
    related_file_ids: [], related_generation_ids: [], is_visible_to_client: false,
  });
  const openEdit = (it) => setEditing({
    ...it,
    related_file_ids: it.related_file_ids || [],
    related_generation_ids: it.related_generation_ids || [],
    is_visible_to_client: !!it.is_visible_to_client,
  });
  const closeDialog = () => setEditing(null);

  const submit = async () => {
    if (!editing?.title?.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    const payload = {
      type: editing.type,
      status: editing.status,
      title: editing.title.trim(),
      description: editing.description?.trim() || '',
      related_file_ids: editing.related_file_ids || [],
      related_generation_ids: editing.related_generation_ids || [],
      is_visible_to_client: !!editing.is_visible_to_client,
    };
    try {
      if (editing.iteration_id) {
        await axios.patch(`${API}/iterations/${editing.iteration_id}`, payload, opts);
        toast.success('Itération mise à jour');
      } else {
        await axios.post(`${API}/projects/${projectId}/iterations`, payload, opts);
        toast.success('Itération ajoutée');
      }
      closeDialog();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    }
  };

  const removeIteration = async (id) => {
    if (!window.confirm('Supprimer cette itération ?')) return;
    try {
      await axios.delete(`${API}/iterations/${id}`, opts);
      toast.success('Itération supprimée');
      load();
    } catch { toast.error('Erreur suppression'); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/iterations/export-pdf`, {
        ...opts, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `iterations_${(project?.name || 'projet').replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Journal exporté');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec export PDF');
    } finally { setExporting(false); }
  };

  const toggleAttachment = (key, id) => {
    setEditing((cur) => {
      const arr = cur?.[key] || [];
      const has = arr.includes(id);
      return { ...cur, [key]: has ? arr.filter((x) => x !== id) : [...arr, id] };
    });
  };

  return (
    <HudCard variant="glass">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <span className="hud-eyebrow hud-eyebrow-gold">Suivi</span>
          <p
            className="mt-1 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
          >
            Itérations & validations client
          </p>
          <p className="text-[12.5px] mt-1 max-w-2xl" style={{ color: 'var(--c-text-2)' }}>
            Journal des allers-retours : briefs additionnels, revues de rendus,
            validations, demandes de retake, livraisons.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HudButton
            variant="ghost"
            size="sm"
            onClick={exportPdf}
            disabled={exporting || items.length === 0}
            leftIcon={exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            data-testid="iterations-export-pdf-btn"
            title="Exporter le journal au format PDF"
          >
            Exporter PDF
          </HudButton>
          <HudButton
            variant="primary"
            size="sm"
            onClick={openCreate}
            leftIcon={<Plus className="w-3.5 h-3.5" />}
            data-testid="iterations-add-btn"
          >
            Ajouter
          </HudButton>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[13px] py-8 justify-center" style={{ color: 'var(--c-text-2)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-lg py-10 text-center"
          style={{ background: 'var(--c-surface-2)', border: '1px dashed var(--c-border)' }}
          data-testid="iterations-empty"
        >
          <Hourglass className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--c-text-muted)' }} />
          <p className="text-[13px] font-medium" style={{ color: 'var(--c-text)' }}>
            Pas encore d'itération enregistrée
          </p>
          <p className="text-[11.5px] mt-1 mb-3" style={{ color: 'var(--c-text-muted)' }}>
            Tracez chaque revue, validation ou demande client pour garder l'historique du projet.
          </p>
          <span className="text-[11px]" style={{ color: 'var(--c-text-faint)' }}>
            Projet créé le {fmtDateTime(project?.created_at)}
          </span>
        </div>
      ) : (
        <ol className="relative ml-2 space-y-3" data-testid="iterations-timeline">
          <span
            aria-hidden
            className="absolute left-[7px] top-2 bottom-2 w-px"
            style={{ background: 'var(--c-border)' }}
          />
          {items.map((it) => (
            <IterationRow
              key={it.iteration_id}
              iteration={it}
              fileLookup={fileLookup}
              genLookup={genLookup}
              onEdit={() => openEdit(it)}
              onDelete={() => removeIteration(it.iteration_id)}
            />
          ))}
        </ol>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent
          data-testid="iteration-dialog"
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>
              {editing?.iteration_id ? 'Modifier l\'itération' : 'Nouvelle itération'}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hud-eyebrow block mb-1.5">Type</label>
                  <Select value={editing.type} onValueChange={(v) => setEditing((e) => ({ ...e, type: v }))}>
                    <SelectTrigger className="h-9" data-testid="iteration-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="hud-eyebrow block mb-1.5">Statut</label>
                  <Select value={editing.status} onValueChange={(v) => setEditing((e) => ({ ...e, status: v }))}>
                    <SelectTrigger className="h-9" data-testid="iteration-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="hud-eyebrow block mb-1.5">Titre</label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing((cur) => ({ ...cur, title: e.target.value }))}
                  placeholder="ex. Revue de la perspective salon T3"
                  className="h-9"
                  data-testid="iteration-title-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="hud-eyebrow block mb-1.5">Description (optionnel)</label>
                <textarea
                  value={editing.description || ''}
                  onChange={(e) => setEditing((cur) => ({ ...cur, description: e.target.value }))}
                  placeholder="Détails, retours client, références, lien vers rendus…"
                  rows={3}
                  className="w-full rounded-lg p-3 text-[13px] outline-none resize-y"
                  style={{
                    background: 'var(--c-surface-2)',
                    border: '1px solid var(--c-border)',
                    color: 'var(--c-text)',
                  }}
                  data-testid="iteration-description-input"
                />
              </div>

              {/* Visibility toggle (Phase 3.6) */}
              <label
                className="flex items-center gap-2.5 cursor-pointer rounded-lg p-2.5"
                style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
                data-testid="iteration-visible-toggle"
              >
                <input
                  type="checkbox"
                  checked={!!editing.is_visible_to_client}
                  onChange={(e) => setEditing((cur) => ({ ...cur, is_visible_to_client: e.target.checked }))}
                  className="w-4 h-4 accent-[#D9A05B] cursor-pointer"
                />
                <span className="flex-1">
                  <span className="flex items-center gap-1.5 text-[13px] font-medium" style={{ color: 'var(--c-text)' }}>
                    <ShieldCheck className="w-3.5 h-3.5" style={{ color: 'var(--c-gold)' }} />
                    Visible côté client (Vue Universelle)
                  </span>
                  <span className="block text-[11px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>
                    Quand activé, cette itération apparaît dans la timeline lecture seule partagée au client.
                  </span>
                </span>
              </label>

              {/* Attachments — files */}
              <AttachmentPicker
                label="Fichiers liés"
                emptyHint="Aucun fichier dans ce projet."
                items={files.map((f) => ({
                  id: f.file_id,
                  label: f.original_name || f.filename || f.file_id,
                  hint: f.doc_type,
                }))}
                selected={editing.related_file_ids || []}
                onToggle={(id) => toggleAttachment('related_file_ids', id)}
                testid="iteration-files-picker"
              />

              {/* Attachments — generations */}
              <AttachmentPicker
                label="Rendus liés"
                emptyHint="Aucun rendu généré pour ce projet."
                items={generations.map((g) => ({
                  id: g.job_id || g.generation_id,
                  label: g.lot_number || g.params?.type_bien || (g.job_id || '').slice(-8),
                  hint: g.params?.vue_angle || g.status,
                }))}
                selected={editing.related_generation_ids || []}
                onToggle={(id) => toggleAttachment('related_generation_ids', id)}
                testid="iteration-generations-picker"
              />
            </div>
          )}
          <DialogFooter>
            <HudButton variant="ghost" size="sm" onClick={closeDialog}>Annuler</HudButton>
            <HudButton
              variant="primary"
              size="sm"
              onClick={submit}
              data-testid="iteration-submit-btn"
            >
              {editing?.iteration_id ? 'Mettre à jour' : 'Ajouter'}
            </HudButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </HudCard>
  );
};

/* Reusable attachments picker — checkbox grid with truncation + scroll */
const AttachmentPicker = ({ label, items = [], selected = [], onToggle, emptyHint, testid }) => {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.label || '').toLowerCase().includes(q) ||
      (it.hint || '').toLowerCase().includes(q)
    );
  }, [items, query]);
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="hud-eyebrow">{label}</label>
        <span className="text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>
          {selected.length} sélectionné{selected.length > 1 ? 's' : ''}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-[11.5px] py-2 px-3 rounded-md" style={{
          background: 'var(--c-surface-2)',
          border: '1px dashed var(--c-border)',
          color: 'var(--c-text-faint)',
        }}>
          {emptyHint}
        </p>
      ) : (
        <>
          {items.length > 6 && (
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filtrer…"
              className="h-8 mb-2 text-[12px]"
              data-testid={`${testid}-filter`}
            />
          )}
          <div
            className="rounded-lg overflow-y-auto"
            style={{
              maxHeight: 160,
              background: 'var(--c-surface-2)',
              border: '1px solid var(--c-border)',
            }}
          >
            {filtered.slice(0, 50).map((it) => {
              const isSel = selected.includes(it.id);
              return (
                <label
                  key={it.id}
                  className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-white/[0.04]"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                >
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => onToggle(it.id)}
                    className="w-3.5 h-3.5 accent-[#D9A05B] cursor-pointer"
                  />
                  <span className="text-[12px] truncate flex-1" style={{ color: 'var(--c-text)' }} title={it.label}>
                    {it.label}
                  </span>
                  {it.hint && (
                    <span className="text-[10px] shrink-0" style={{ color: 'var(--c-text-faint)' }}>
                      {it.hint}
                    </span>
                  )}
                </label>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-[11px] py-3 px-3 italic" style={{ color: 'var(--c-text-faint)' }}>
                Aucun résultat
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const IterationRow = ({ iteration, fileLookup = {}, genLookup = {}, onEdit, onDelete }) => {
  const meta = TYPE_META[iteration.type] || TYPE_META.note;
  const statusMeta = STATUS_META[iteration.status] || STATUS_META.en_attente;
  const Icon = meta.icon;
  const fromClient = iteration.source === 'client_universal_view';
  const fileCount = (iteration.related_file_ids || []).length;
  const genCount = (iteration.related_generation_ids || []).length;
  return (
    <li
      className="relative pl-7 group"
      data-testid={`iteration-row-${iteration.iteration_id}`}
    >
      <span
        aria-hidden
        className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full flex items-center justify-center"
        style={{
          background: 'var(--c-surface-1)',
          border: `1.5px solid ${meta.color}`,
          boxShadow: `0 0 8px ${meta.color}55`,
        }}
      >
        <Icon className="w-2.5 h-2.5" style={{ color: meta.color }} />
      </span>
      <div
        className="rounded-lg p-3"
        style={{
          background: 'var(--c-surface-2)',
          border: '1px solid var(--c-border)',
        }}
      >
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span
                className="text-[10.5px] uppercase tracking-[0.16em] font-semibold"
                style={{ color: meta.color }}
              >
                {meta.label}
              </span>
              <HudBadge tone={statusMeta.tone}>{statusMeta.label}</HudBadge>
              {fromClient && (
                <HudBadge tone="info" data-testid={`iteration-source-client-${iteration.iteration_id}`}>
                  Depuis Vue Universelle
                </HudBadge>
              )}
              {iteration.is_visible_to_client && !fromClient && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold"
                  style={{ color: 'var(--c-gold)' }}
                  title="Visible côté client"
                  data-testid={`iteration-visible-${iteration.iteration_id}`}
                >
                  <ShieldCheck className="w-2.5 h-2.5" />
                  Visible client
                </span>
              )}
              <span className="text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>
                {fmtDateTime(iteration.created_at)}
              </span>
            </div>
            <p className="text-[13.5px] font-medium leading-snug" style={{ color: 'var(--c-text)' }}>
              {iteration.title}
            </p>
            {iteration.description && (
              <p className="text-[12px] mt-1 leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--c-text-2)' }}>
                {iteration.description}
              </p>
            )}
            {(fileCount > 0 || genCount > 0) && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`iteration-attachments-${iteration.iteration_id}`}>
                {(iteration.related_file_ids || []).slice(0, 6).map((fid) => {
                  const f = fileLookup[fid];
                  return (
                    <a
                      key={fid}
                      href={f ? `${API}/files/${fid}/download` : undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 rounded hover:bg-white/[0.06] transition-colors"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid var(--c-border)',
                        color: 'var(--c-text-2)',
                      }}
                      title={f?.original_name || fid}
                    >
                      <FileText className="w-2.5 h-2.5" />
                      {f ? (f.original_name || f.filename || fid).slice(0, 24) : fid.slice(-8)}
                    </a>
                  );
                })}
                {(iteration.related_generation_ids || []).slice(0, 4).map((gid) => {
                  const g = genLookup[gid];
                  return (
                    <span
                      key={gid}
                      className="inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 rounded"
                      style={{
                        background: 'rgba(217,160,91,0.08)',
                        border: '1px solid rgba(217,160,91,0.30)',
                        color: 'var(--c-gold)',
                      }}
                      title={g?.lot_number || gid}
                    >
                      <Package className="w-2.5 h-2.5" />
                      {g?.lot_number || gid.slice(-8)}
                    </span>
                  );
                })}
                {fileCount + genCount > 10 && (
                  <span className="text-[10px]" style={{ color: 'var(--c-text-faint)' }}>
                    +{fileCount + genCount - 10}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0 opacity-50 group-hover:opacity-100 transition-opacity">
            <button
              onClick={onEdit}
              className="p-1.5 rounded hover:bg-white/[0.06] transition-colors"
              title="Modifier"
              data-testid={`iteration-edit-${iteration.iteration_id}`}
            >
              <Pencil className="w-3.5 h-3.5" style={{ color: 'var(--c-text-muted)' }} />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 rounded hover:bg-red-500/10 transition-colors"
              title="Supprimer"
              data-testid={`iteration-delete-${iteration.iteration_id}`}
            >
              <Trash2 className="w-3.5 h-3.5" style={{ color: 'var(--c-text-muted)' }} />
            </button>
          </div>
        </div>
      </div>
    </li>
  );
};

